'use client';

import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useState, useEffect, useRef } from 'react';
import { FiMenu, FiX, FiShoppingBag, FiHeart, FiSearch } from 'react-icons/fi';
import Wishlist from './Wishlist';
import Cart from './Cart';
import { useShop } from '@/context/ShopContext';
import { Spinner } from '@/components/ui/Spinner';
import { useLanguage } from '@/context/LanguageContext';
import { useProductSearch } from '@/hooks/useProducts';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO_SRC = 'https://res.cloudinary.com/dm7ttgpta/image/upload/v1769631342/Logo_Simte_Poezia_Florilor_V2_Negru_d4urcr.png';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { getCartItemCount } = useShop();
  const pathname = usePathname();
  const router = useRouter();
  const prevPathnameRef = useRef(pathname);
  const { t } = useLanguage();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      setHydrated(true);
    }, 0);
  }, []);
  
  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results
  const { data: searchResults, isLoading: isSearching } = useProductSearch(debouncedQuery);
  
  const navLinks = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.shop'), href: '/shop' },
    { name: t('nav.extras'), href: '/extras' },
    { name: t('nav.collections'), href: '/collections' },
    { name: t('nav.events'), href: '/events' },
    { name: t('nav.blog'), href: '/blog' },
    { name: t('nav.contact'), href: '/contact' },
    { name: t('nav.help'), href: '/help' },
    { name: t('nav.trackOrder'), href: '/orders' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      if (prevPathnameRef.current !== pathname && isOpen) {
        setIsOpen(false);
      }
      prevPathnameRef.current = pathname;
    };
    
    handleRouteChange();
  }, [pathname, isOpen]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };

    if (searchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchOpen]);

  const handleSearchResultClick = (productId: string, slug: string) => {
    router.push(`/shop/${productId}?slug=${slug}`);
    setSearchOpen(false);
    setSearchQuery('');
  };

  // Show loading state during hydration
  if (!hydrated) {
    return null; // or return a placeholder
  }

  return (
    <header
      className={`organic fixed left-0 right-0 z-50 transition-all duration-300 bg-[var(--primary-background)] ${
        scrolled ? 'backdrop-blur-md shadow-sm' : ''
      }`}
      style={{ top: 'var(--store-banner-h, 0px)', borderBottom: '1px solid var(--color-divider)' }}
    >
      <Wishlist isOpen={wishlistOpen} onClose={() => setWishlistOpen(false)} />
      <Cart isOpen={cartOpen} onClose={() => setCartOpen(false)} />

     <div className="max-w-7xl mx-auto" style={{ paddingInline: 'clamp(20px, 4vw, 48px)' }}>
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Image
              src={LOGO_SRC}
              alt="Buchetul Simonei Logo"
              width={125}
              height={50}
              className="object-contain"
              priority
            />
          </Link>


          {/* Right side icons */}
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            {/* Search button */}
            <button
              className="btn btn-icon"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Search"
            >
              <FiSearch className="h-4 w-4" />
            </button>

            <button
              className="btn btn-icon relative"
              onClick={() => setWishlistOpen(true)}
              aria-label="Wishlist"
            >
              <FiHeart className="h-4 w-4" />
            </button>

            <button
              className="btn btn-icon relative"
              onClick={() => setCartOpen(true)}
              aria-label="Cart"
            >
              <FiShoppingBag className="h-4 w-4" />
              {getCartItemCount() > 0 && hydrated && (
                <span className="absolute -top-1 -right-1 bg-[var(--color-accent)] text-[var(--color-bg)] text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getCartItemCount() > 9 ? '9+' : getCartItemCount()}
                </span>
              )}
            </button>

            <LanguageSwitcher />

            {/* Menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="btn btn-icon"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <FiX className="h-5 w-5" aria-hidden="true" />
              ) : (
                <FiMenu className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Search dropdown */}
      {searchOpen && (
        <div 
          ref={searchRef}
          className="absolute top-16 left-0 right-0 bg-[var(--card)] shadow-lg border-t border-[var(--border)] animate-slide-down"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--muted-foreground)] h-5 w-5" />
              <input
                type="text"
                placeholder="Caută buchete, flori..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent bg-[var(--card)] text-[var(--foreground)]"
                autoFocus
              />
            </div>

            {/* Search Results */}
            {searchQuery.trim() && (
              <div className="mt-4 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="flex justify-center py-8">
                    <Spinner className="w-6 h-6" />
                  </div>
                ) : (searchResults?.products?.length > 0 || searchResults?.extras?.length > 0) ? (
                  <div className="space-y-4">
                    {/* Products Section */}
                    {searchResults.products?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide px-3 mb-2">
                          {t('nav.shop') || 'Products'}
                        </h3>
                        <div className="space-y-2">
                          {searchResults.products.map((product: { productId: string; slug: string; name: string; category: string; images: string[]; price: number }) => (
                            <button
                              key={product.productId}
                              onClick={() => handleSearchResultClick(product.productId, product.slug)}
                              className="w-full flex items-center space-x-4 p-3 hover:bg-[var(--accent)] rounded-lg transition-colors text-left"
                            >
                              <Image
                                src={product.images[0]}
                                alt={product.name}
                                width={60}
                                height={60}
                                className="rounded-md object-cover shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-[var(--foreground)] truncate">
                                  {product.name}
                                </h4>
                                <p className="text-sm text-[var(--muted-foreground)]">{product.category}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium text-[var(--primary)]">
                                  {(product.price / 100).toFixed(2)} RON
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Extras Section */}
                    {searchResults.extras?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wide px-3 mb-2">
                          {t('nav.extras') || 'Extras'}
                        </h3>
                        <div className="space-y-2">
                          {searchResults.extras.map((extra: { extraId: string; slug: string; name: string; category: string; images: string[]; price: number }) => (
                            <div
                              key={extra.extraId}
                              className="w-full flex items-center space-x-4 p-3 hover:bg-[var(--accent)] rounded-lg transition-colors"
                            >
                              <div className="relative w-[60px] h-[60px] shrink-0">
                                <Image
                                  src={extra.images[0] || '/placeholder.jpg'}
                                  alt={extra.name}
                                  fill
                                  className="rounded-md object-cover"
                                />
                                <div className="absolute -top-1 -right-1 bg-[var(--primary)] text-[var(--primary-foreground)] text-xs px-1.5 py-0.5 rounded-full">
                                  🎁
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-[var(--foreground)] truncate">
                                  {extra.name}
                                </h4>
                                <p className="text-sm text-[var(--muted-foreground)] capitalize">{extra.category}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium text-[var(--primary)]">
                                  {(extra.price / 100).toFixed(2)} RON
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[var(--muted-foreground)]">
                    Nu s-au găsit rezultate pentru &quot;{searchQuery}&quot;
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu panel */}
      <div
        className={`transition-all bg-[var(--card)] duration-300 ease-in-out ${
          isOpen ? 'max-h-100 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              href={link.href}
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                pathname === link.href
                  ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                  : 'text-[var(--foreground)] hover:bg-[var(--accent)]'
              }
              ${link.href === '/orders' ? 'underline' : ''}
              `}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}